import { HttpClient } from '@angular/common/http'
import { BehaviorSubject, firstValueFrom, map, tap } from 'rxjs'
import { Display, Facility } from 'app/models'
import { Injectable, inject } from '@angular/core'
import { LocalStorageService } from './local_storage_service'
import { MessageService } from './message_service'
import { BroadcastService } from './broadcast_service'
import { CentrifugoService, RealtimeChannel } from 'common/realtime/centrifugo.service'
import { environment } from 'environments/environment'

@Injectable({providedIn: 'root'})
export class TvService {
  private _pingInterval: any
  private _stateChannel?: RealtimeChannel
  private _eventsChannel?: RealtimeChannel
  private _channelName?: string

  // undefined = not yet resolved (important for auth), null = signed out
  private readonly _currentTv$ = new BehaviorSubject<Display | undefined | null>(undefined)
  private readonly _displays$ = new BehaviorSubject<Display[]>([])

  readonly currentTv$ = this._currentTv$.asObservable()
  readonly displays$ = this._displays$.asObservable()

  private $http = inject(HttpClient)
  private localStorageService = inject(LocalStorageService)
  private Message = inject(MessageService)
  private events = inject(BroadcastService)
  private centrifugo = inject(CentrifugoService)

  get currentTv () { return this._currentTv$.value }

  constructor () {
    const saved = this.localStorageService.getItem('displays') as Display[]
    if (saved) {
      this._displays$.next(saved)
    }
  }

  switch (shortId: string) {
    const found = this._displays$.value.find((d) => d.short_id === shortId)
    this.set(found)
    return this.currentTv
  }

  list () {
    firstValueFrom(this.$http.get<Display[]>('/api/tv')).then(liveDisplays => {
      // Make sure we update the cached example here
      const curKeys = liveDisplays.map(d => d.short_id)
      // Remove existing displays not in our list
      const temp = this._displays$.value.filter(d => !curKeys.includes(d.short_id));
      for (const existing of temp) {
        const update = liveDisplays.find(ld => ld.short_id === existing.short_id)
        Object.assign(existing, update)
      }
      const tmpKeys = temp.map(d => d.short_id)
      // All items not updated above and add them
      const newTvs = liveDisplays.filter(d => !tmpKeys.includes(d.short_id))
      for (const live of newTvs) {
        temp.push(live)
      }
      this.setList(temp)
    })
  }

  setFacility (facility: Facility) {
    this.setList(facility.displays)
  }

  private setList (displays: Display[]) {
    this._displays$.next([...displays])
    this.localStorageService.setItem('displays', displays)
  }

  get () {
    return firstValueFrom(this.$http.get<Display>('/api/tv/current').pipe(map((display) => {
      const xd = this._displays$.value.find((d) => d.short_id === display.short_id)
      // current never returns auth_token — merge so it survives (the display we
      // fetched is by definition the one whose token authenticated the request)
      return xd ? Object.assign(xd, display) : new Display({ ...this._currentTv$.value, ...display })
    }), tap((d) => {
      const _displays = [...this._displays$.value]
      for (let i = 0; i < _displays.length; i++) {
        if (_displays[i].short_id == d.short_id) {
          _displays[i] = d
          this.setList(_displays)
          break
        }
      }
      // TODO add the display if not in this list?
    }))).then(r => new Display(r), r => { throw r.error })
  }

  create (tv: Partial<Display>) {
    return firstValueFrom(this.$http.post('/api/tv', {tv})).then(r => {
      const tv = new Display(r)
      this.initialiseTv(tv)
      return tv
    }, r => { throw r.error })
  }

  initialiseTv (tv: Display) {
    this._displays$.next([...this._displays$.value, tv])
  }

  set (tv: Display) {
    if (tv) {
      this._currentTv$.next(tv)
      if (tv.message) {
        this.Message.show(tv.message)
      }
      this.ensureMonitoring(tv)
    } else {
      this.localStorageService.removeItem('tv')
      this._currentTv$.next(null)
      this.cancelMonitoring()
    }
  }

  signOut () {
    this.cancelMonitoring()
    this._displays$.next([])
    this.localStorageService.removeItem('tv')
    this.localStorageService.removeItem('displays')
    this._currentTv$.next(undefined)
  }

  // Private ------------------------------------------------------------------------

  // tv:<short_id> carries the full display state document; tv:<short_id>:events
  // carries typed page events, dispatched locally via TvEventsService. No
  // server-side recovery: every (re)subscribe refetches state from the API (the
  // source of truth) and tells pages to refetch their data — one code path heals
  // boot, dropped wifi and server restarts alike.
  private ensureMonitoring (tv: Display) {
    const channelName = `tv:${tv.short_id}`
    if (this._channelName === channelName) { return }

    this.cancelMonitoring()
    this._channelName = channelName
    this._stateChannel = this.centrifugo.subscribe(channelName).subscribe((doc: any) => this.applyState(doc))
    this._stateChannel.sub.on('subscribed', () => {
      this.get().then((d) => this._currentTv$.next(d), () => { /* transient — retried on next (re)subscribe */ })
      this.events.emit({ type: 'reconnected' })
    })
    this._eventsChannel = this.centrifugo.subscribe(`${channelName}:events`)
      .subscribe((event: any) => {
        // "IR remote" reload button: an explicit command, so no version guard
        if (event?.type === 'reload') { window.location.reload(); return }
        this.events.emit({ type: event?.type, data: event?.data })
      })
    // Slow liveness ping so the club dashboard can see the TV is alive
    this._pingInterval = setInterval(() => this.ping(), 5 * 60 * 1000)
  }

  private cancelMonitoring () {
    if (this._pingInterval) {
      clearInterval(this._pingInterval)
      this._pingInterval = undefined
    }
    this._stateChannel?.unsubscribe()
    this._eventsChannel?.unsubscribe()
    this._stateChannel = this._eventsChannel = this._channelName = undefined
    // The connection token is minted for one TV identity — tear the connection
    // down so a re-registration reconnects and re-authenticates as the new TV.
    this.centrifugo.disconnect()
  }

  private applyState (doc: any) {
    const previous = this._currentTv$.value
    // The state doc never carries auth_token — merge over the current display so
    // credentials (and anything else the doc omits) survive a state publish.
    const display = new Display({ ...previous, ...doc })
    this._currentTv$.next(display)
    // The message is part of the state doc; reflect it whenever it changes
    if (JSON.stringify(display.message) !== JSON.stringify(previous?.message)) {
      this.Message.show(display.message)
    }
  }

  private ping () {
    firstValueFrom(this.$http.get<{version: string}>('/api/tv/ping'))
      .then((r) => this.reloadIfOutdated(r?.version), () => { /* transient — next ping retries */ })
  }

  // TVs are set-and-forget: when the server ships a new build, reload to pick it
  // up. Two guards against reload churn: only reload FORWARD (frontend-only
  // hotfixes leave the app ahead of the backend — that must not reload), and at
  // most once per server version (a reload that didn't get a new bundle won't loop).
  private reloadIfOutdated (serverVersion: string) {
    if (!serverVersion || !this.isNewer(serverVersion, environment.version)) { return }
    if (this.localStorageService.getItem('reloaded_for') === serverVersion) { return }
    this.localStorageService.setItem('reloaded_for', serverVersion)
    window.location.reload()
  }

  private isNewer (server: string, built: string) {
    const s = server.split('.').map(Number)
    const b = built.split('.').map(Number)
    for (let i = 0; i < Math.max(s.length, b.length); i++) {
      const diff = (s[i] || 0) - (b[i] || 0)
      if (diff) { return diff > 0 }
    }
    return false
  }
}
